/**
 * Basemap presets shared by every map in the product.
 *
 * One declaration, so a tile provider is swapped in one place — the four map
 * screens used to each hard-code their own URLs and drifted apart.
 */

/** Ouagadougou-ish. The corridor the fleet actually runs is centred here. */
export const DEFAULT_CENTER = [12.5, -1.5];
export const DEFAULT_ZOOM = 6;

/**
 * Les tuiles Esri s'affichent sans clé. Une clé (`REACT_APP_ESRI_API_KEY`,
 * compte gratuit ArcGIS Location Platform) reste facultative: elle sert au
 * quota et au suivi d'usage chez Esri, jamais à l'affichage.
 *
 * Attention: CARTO (`basemaps.cartocdn.com`) exige désormais lui aussi une clé
 * et barre ses tuiles de « API KEY REQUIRED »: il n'est plus utilisé ici.
 */
const CLE_ESRI = (process.env.REACT_APP_ESRI_API_KEY || '').trim();
const JETON_ESRI = CLE_ESRI ? `?token=${CLE_ESRI}` : '';
const ESRI = 'https://server.arcgisonline.com/ArcGIS/rest/services';

/**
 * Attributions complètes, avec leurs liens: la plupart des fournisseurs de
 * tuiles les exigent (OpenStreetMap: licence ODbL, CARTO et OpenTopoMap: crédit
 * du style). Leaflet les affiche en bas à droite de chaque carte.
 */
const lien = (adresse, texte) =>
  `<a href="${adresse}" target="_blank" rel="noopener noreferrer">${texte}</a>`;

export const ATTRIBUTION_OSM = `&copy; ${lien(
  'https://www.openstreetmap.org/copyright',
  'OpenStreetMap',
)} contributors`;
export const ATTRIBUTION_ESRI = `&copy; ${lien(
  'https://www.esri.com/',
  'Esri',
)}, Maxar, Earthstar Geographics et la communauté des utilisateurs SIG`;
export const ATTRIBUTION_ESRI_SOMBRE = `&copy; ${lien(
  'https://www.esri.com/',
  'Esri',
)}, HERE, Garmin, ${lien('https://www.openstreetmap.org/copyright', 'OpenStreetMap')} contributors`;
export const ATTRIBUTION_OPENTOPOMAP = `&copy; ${lien(
  'https://opentopomap.org',
  'OpenTopoMap',
)} (${lien('https://creativecommons.org/licenses/by-sa/3.0/', 'CC-BY-SA')})`;

const SATELLITE = {
  label: 'Satellite',
  url: `${ESRI}/World_Imagery/MapServer/tile/{z}/{y}/{x}${JETON_ESRI}`,
  // Les noms de lieux viennent aussi d'Esri (données OpenStreetMap): crédités ici.
  attribution: `${ATTRIBUTION_ESRI}, ${ATTRIBUTION_OSM}`,
  maxZoom: 19,
  /**
   * L'imagerie ne porte aucun nom de lieu: une couche d'étiquettes
   * transparente se pose dessus (Esri « World_Boundaries_and_Places »), une
   * requête par tuile, l'image reste lisible.
   */
  labels: {
    url: `${ESRI}/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}${JETON_ESRI}`,
    opacity: 0.95,
    maxZoom: 19,
  },
};

export const MAP_LAYERS = {
  satellite: SATELLITE,
  streets: {
    label: 'Plan',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: ATTRIBUTION_OSM,
    maxZoom: 19,
  },
  topo: {
    label: 'Topographique',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: `${ATTRIBUTION_OSM}, ${ATTRIBUTION_OPENTOPOMAP}`,
    maxZoom: 17,
  },
  dark: {
    label: 'Sombre',
    url: `${ESRI}/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}${JETON_ESRI}`,
    attribution: ATTRIBUTION_ESRI_SOMBRE,
    // Fond gris foncé d'Esri: sa couverture native s'arrête au zoom 16.
    maxZoom: 16,
    labels: {
      url: `${ESRI}/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}${JETON_ESRI}`,
      opacity: 1,
      maxZoom: 16,
    },
  },
};

export const resolveLayer = (key) => MAP_LAYERS[key] || MAP_LAYERS.streets;

/** Exposé pour vérifier les crédits des fonds dans les tests. */
export { SATELLITE as FOND_SATELLITE };
